/**
 * Helpers puros do sync de mensagens do chat.
 * Sem React — só merge, dedupe, hash, overrides e fetch no banco.
 */
import { chatRepository } from '../../../services/repositories/ChatRepository';
import { supabase } from '../../../lib/supabase';
import { generateJidVariants } from '../../../utils/phoneUtils';
import { stripMessageSignature } from '../../../utils/messageSignature';
import { isAbortError } from '../../../utils/networkError';
import { preferDisplayMediaUrl } from '../../../utils/failedImageCache';
import {
    scopeConversationQuery,
    shouldQueryOrphanConversation,
} from '../lib/conversationScope';
import { ChatMessage } from './types';

const PENDING_KEEP_MS = 120_000;

/** temp-* / sent-* — ainda não reconciliados com id do provedor. */
function isLocalPlaceholderId(id: string): boolean {
    return id.startsWith('temp-') || id.startsWith('sent-');
}

/** Abort do fetch não deve estourar na UI. */
function shouldSwallowAbort(error: unknown, signal?: AbortSignal): boolean {
    return Boolean(signal?.aborted || isAbortError(error));
}

export const hintKey = (text: string) => stripMessageSignature(text).trim();

export function mergePatch(
    map: Map<string, Partial<ChatMessage>>,
    key: string,
    patch: Partial<ChatMessage>,
) {
    map.set(key, { ...map.get(key), ...patch });
}

/**
 * Fingerprint barato do espelho de mensagens.
 * JSON.stringify no histórico inteiro a cada merge trava a UI; basta id/hora/status/mídia.
 * media.url no hash — senão R2 chega e a UI fica em "indisponível".
 */
export const messagesHash = (msgs: ChatMessage[]) =>
    msgs
        .map((m) =>
            [
                m.id,
                m.timestamp instanceof Date ? m.timestamp.getTime() : String(m.timestamp ?? ''),
                m.status ?? '',
                m.text?.length ?? 0,
                m.media?.url ?? '',
                m.is_edited ? '1' : '0',
            ].join('\u001f'),
        )
        .join('\u001e');

function compareByMessageTime(a: ChatMessage, b: ChatMessage): number {
    // IDs dos provedores são opacos; empates preservam a sequência recebida.
    return a.timestamp.getTime() - b.timestamp.getTime();
}

export function applyMessageOverrides(
    messages: ChatMessage[],
    byId: Map<string, Partial<ChatMessage>>,
    byHint: Map<string, Partial<ChatMessage>>,
): ChatMessage[] {
    if (byId.size === 0 && byHint.size === 0) return messages;
    return messages.map((m) => {
        const idPatch = byId.get(m.id);
        if (idPatch) return { ...m, ...idPatch };
        if (m.sender === 'me' && m.text) {
            const hintPatch = byHint.get(hintKey(m.text));
            if (hintPatch) {
                mergePatch(byId, m.id, hintPatch);
                return { ...m, ...hintPatch };
            }
        }
        return m;
    });
}

/** Key estável na lista: sobrevive temp-* → id real sem remount (evita piscada). */
export function stableMessageKey(message: ChatMessage): string {
    return message.clientMessageId || message.id;
}

function sameLogicalMessage(a: ChatMessage, b: ChatMessage): boolean {
    return a.id === b.id
        || Boolean(a.clientMessageId && a.clientMessageId === b.id)
        || Boolean(b.clientMessageId && b.clientMessageId === a.id)
        || Boolean(a.clientMessageId && a.clientMessageId === b.clientMessageId);
}

/** Mantém blob local / R2 na UI enquanto o banco ainda não tem media_url durável. */
export function preferMessageMedia(
    current?: ChatMessage['media'],
    incoming?: ChatMessage['media'],
): ChatMessage['media'] | undefined {
    const url = preferDisplayMediaUrl(current?.url, incoming?.url);
    if (!url) return undefined;
    if (url === incoming?.url && incoming) return incoming;
    if (url === current?.url && current) {
        return incoming ? { ...incoming, ...current, url } : current;
    }
    return { ...(current || {}), ...(incoming || {}), url };
}

const STATUS_RANK: Record<ChatMessage['status'], number> = {
    failed: -1,
    pending: 0,
    uncertain: 0,
    sent: 1,
    delivered: 2,
    read: 3,
};

/** Erro local ganha; senão avança pending → sent → delivered → read. */
export function preferMessageStatus(
    local?: ChatMessage['status'],
    remote?: ChatMessage['status'],
): ChatMessage['status'] {
    if (!local) return remote ?? 'sent';
    if (!remote) return local;
    if (local === 'failed' || local === 'uncertain') return local;
    return (STATUS_RANK[remote] ?? 0) >= (STATUS_RANK[local] ?? 0) ? remote : local;
}

function enrichFromPrev(msg: ChatMessage, prev: ChatMessage[]): ChatMessage {
    const match = prev.find((p) => sameLogicalMessage(p, msg));
    const clientMessageId = msg.clientMessageId || match?.clientMessageId;
    const media = preferMessageMedia(match?.media, msg.media);
    if (clientMessageId === msg.clientMessageId && media === msg.media) return msg;
    return {
        ...msg,
        ...(clientMessageId ? { clientMessageId } : {}),
        media,
    };
}

/** Nunca descarta envios recentes ainda não confirmados no banco */
export function mergeFetchedWithDisplay(
    prev: ChatMessage[],
    fetched: ChatMessage[],
    pending: ChatMessage[],
): ChatMessage[] {
    const map = new Map(fetched.map((m) => [m.id, enrichFromPrev(m, prev)]));
    for (const opt of pending) {
        if (!map.has(opt.id)) map.set(opt.id, enrichFromPrev(opt, prev));
    }
    const oldestFetched = fetched.length ? Math.min(...fetched.map((m) => m.timestamp.getTime())) : -Infinity;
    const now = Date.now();
    for (const m of prev) {
        const localId = isLocalPlaceholderId(m.id);
        const loadedHistory = !localId && m.timestamp.getTime() < oldestFetched;
        const recentSend = !localId && m.sender === 'me' && now - m.timestamp.getTime() <= PENDING_KEEP_MS;
        if (!map.has(m.id) && (loadedHistory || recentSend)) map.set(m.id, m);
    }

    // Preserva ordem visual já na tela; só acrescenta o que é novo (sem re-sort que “pisca”).
    // Preferir id real sobre temp-* quando os dois ainda estão no map.
    const used = new Set<string>();
    const ordered: ChatMessage[] = [];
    for (const prevMsg of prev) {
        const matches = [...map.values()].filter((m) => sameLogicalMessage(m, prevMsg));
        const hit = matches.find((m) => !isLocalPlaceholderId(m.id))
            ?? matches[0]
            ?? map.get(prevMsg.id);
        if (hit && !used.has(hit.id)) {
            ordered.push(hit);
            for (const m of matches.length ? matches : [hit]) used.add(m.id);
        }
    }
    for (const m of dedupeMessages([...map.values()])) {
        if (used.has(m.id)) continue;
        if (ordered.some((o) => sameLogicalMessage(o, m))) continue;
        ordered.push(m);
        used.add(m.id);
    }
    return ordered;
}

export function dedupeMessages(messages: ChatMessage[]): ChatMessage[] {
    const byId = new Map<string, ChatMessage>();
    for (const msg of messages) byId.set(msg.id, msg);
    return [...byId.values()].sort(compareByMessageTime);
}

/** Atualiza mensagem completa sem perder metadados e reordena pelo horário do provedor. */
export function upsertRealtimeMessage(
    messages: ChatMessage[],
    incoming: ChatMessage,
): ChatMessage[] {
    const existing = messages.find((m) => m.id === incoming.id);
    const merged = { ...existing, ...incoming,
        media: preferMessageMedia(existing?.media, incoming.media),
        quotedMessage: incoming.quotedMessage ?? existing?.quotedMessage,
        adMetadata: incoming.adMetadata ?? existing?.adMetadata,
        reactions: incoming.reactions ?? existing?.reactions,
        clientMessageId: incoming.clientMessageId || existing?.clientMessageId,
    };
    return dedupeMessages(existing
        ? messages.map((m) => m.id === incoming.id ? merged : m)
        : [...messages, incoming]);
}

/** ISO / Date do provedor (PartyKit); inválido → null. */
export function parseProviderTimestamp(value?: string | Date | null): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Aplica horário do provedor sem alterar texto/mídia.
 * Inbound (`them`): pode reordenar (eco atrasa e o relógio do provedor é a verdade).
 * Outbound (`me`): só atualiza o relógio — reordenar aqui faz o último envio
 * “pular” para cima enquanto vizinhos ainda estão otimistas (ex.: form → oi → form → oi).
 * A ordem final vem do fetch do banco em seguida.
 */
export function applyProviderTimestampToMessages(
    messages: ChatMessage[],
    messageId: string,
    timestamp: Date,
): ChatMessage[] {
    const target = messages.find((m) => m.id === messageId);
    if (!messageId || !target) return messages;
    const next = messages.map((m) => (m.id === messageId ? { ...m, timestamp } : m));
    if (target.sender === 'me') return next;
    return dedupeMessages(next);
}

type OrphanConversationRow = { id: string; remote_jid?: string };

/**
 * Única fonte do fallback DB: conversa com connection_id null (pré-escopo).
 * Callers passam select/order/limit; comportamento permanece o de cada caminho.
 */
export async function fetchOrphanConversationsByRemoteJid(
    remoteJid: string,
    organizationId: string,
    options?: {
        select?: string;
        orderBy?: string;
        limit?: number;
        signal?: AbortSignal;
    },
): Promise<{ data: OrphanConversationRow[] | null; error: { message?: string } | null }> {
    const select = options?.select ?? 'id';
    const orderBy = options?.orderBy ?? 'last_message_time';
    let query = supabase
        .from('chat_conversations')
        .select(select)
        .in('remote_jid', generateJidVariants(remoteJid))
        .eq('organization_id', organizationId)
        .is('connection_id', null);
    if (options?.signal) query = query.abortSignal(options.signal);
    query = query.order(orderBy, { ascending: false });
    if (options?.limit != null) query = query.limit(options.limit);
    const { data, error } = await query;
    return { data: (data as OrphanConversationRow[] | null) ?? null, error };
}

/** Busca mensagens do banco, unificando conversas duplicadas por variantes de JID. */
export async function fetchConversationMessages(
    activeConversationId: string,
    dbId: string | null,
    orgId?: string,
    connectionId?: string | null,
    signal?: AbortSignal,
): Promise<ChatMessage[]> {
    if (dbId) return chatRepository.getMessages(dbId, orgId, 80, 0, signal);

    let query = supabase
        .from('chat_conversations')
        .select('id')
        .in('remote_jid', generateJidVariants(activeConversationId));
    query = scopeConversationQuery(query, connectionId ?? null, orgId);
    if (signal) query = query.abortSignal(signal);

    let { data: convs, error } = await query.order('last_message_time', { ascending: false });
    if (error) {
        if (shouldSwallowAbort(error, signal)) return [];
        throw error;
    }
    if (shouldQueryOrphanConversation(Boolean(convs?.length), connectionId, orgId)) {
        const orphan = await fetchOrphanConversationsByRemoteJid(activeConversationId, orgId!, {
            select: 'id',
            orderBy: 'last_message_time',
            signal,
        });
        if (orphan.error) {
            if (shouldSwallowAbort(orphan.error, signal)) return [];
            throw orphan.error;
        }
        convs = orphan.data;
    }
    const convIds = [...new Set(convs?.map((c) => c.id) || [])];
    if (!convIds.length) return [];

    // Primeiro id ganha (variantes JID podem repetir a mesma mensagem).
    const byId = new Map<string, ChatMessage>();
    for (const msg of (await Promise.all(
        convIds.map((id) => chatRepository.getMessages(id, orgId, 80, 0, signal)),
    )).flat()) {
        if (!byId.has(msg.id)) byId.set(msg.id, msg);
    }
    return [...byId.values()].sort(compareByMessageTime);
}
